import { useEffect, useState } from "react";
import { BarChart3, Copy, Edit3, Loader2, Plus, Search, Star, Tags, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";

const darkInput = "w-full rounded-lg border border-adminborder bg-adminbg px-3 py-2 text-sm text-slate-200 outline-none focus:border-saffron";
const CATEGORIES = ["Domestic", "International", "Pricing & Payments", "Weight & Volume", "Pickup", "Delivery",
  "Tracking", "Customs", "Packing", "NRI Shipping", "eCommerce", "Business", "Prohibited Items", "Claims & Refunds", "General"];
const emptyFaq = { question: "", answer: "", category: "Domestic", keywords: "", related_label: "", related_href: "", featured: false, published: true, display_order: 0 };

export default function AdminFaqs({ standalone = false }) {
  const [items, setItems] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({ category: "", q: "", status: "" });
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showMetrics, setShowMetrics] = useState(false);
  const [catModal, setCatModal] = useState(false);
  const [newCat, setNewCat] = useState({ name: "", display_order: 0 });
  const [catDrafts, setCatDrafts] = useState({});

  const loadCategories = () => {
    api.get("/faqs/admin/categories").then((r) => {
      setCategories(r.data);
      setCatDrafts(Object.fromEntries(r.data.map((c) => [c.id, { name: c.name, display_order: c.display_order || 0 }])));
    }).catch(() => {});
  };
  const load = () => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && p.set(k, v));
    api.get(`/faqs/admin/list?${p}`).then((r) => setItems(r.data)).catch(() => {});
    api.get("/faqs/admin/metrics").then((r) => setMetrics(r.data)).catch(() => {});
  };
  useEffect(load, [filters]);
  useEffect(() => { loadCategories(); }, []);
  const categoryOptions = categories.length ? categories.map((c) => c.name) : CATEGORIES;

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, keywords: String(form.keywords || "").split(",").map((k) => k.trim()).filter(Boolean),
                        display_order: parseInt(form.display_order) || 0 };
      delete payload.id; delete payload.views; delete payload.created_at; delete payload.updated_at;
      if (form.id) await api.put(`/faqs/admin/faqs/${form.id}`, payload);
      else await api.post("/faqs/admin/faqs", payload);
      toast.success(form.id ? "FAQ updated" : "FAQ added");
      setForm(null); load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const del = async (f) => {
    if (!window.confirm(`Delete "${f.question}"?`)) return;
    try { await api.delete(`/faqs/admin/faqs/${f.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const addCategory = async () => {
    try {
      await api.post("/faqs/admin/categories", { ...newCat, display_order: parseInt(newCat.display_order) || 0 });
      toast.success("Category added");
      setNewCat({ name: "", display_order: 0 });
      loadCategories();
    } catch (err) { toast.error(apiError(err)); }
  };

  const saveCategory = async (c) => {
    try {
      const draft = catDrafts[c.id] || {};
      await api.put(`/faqs/admin/categories/${c.id}`, { name: draft.name, display_order: parseInt(draft.display_order) || 0 });
      toast.success("Category updated");
      if (filters.category === c.name) setFilters({ ...filters, category: draft.name });
      loadCategories(); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const delCategory = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"? FAQs using it must be moved first.`)) return;
    try {
      await api.delete(`/faqs/admin/categories/${c.id}`);
      toast.success("Category deleted");
      if (filters.category === c.name) setFilters({ ...filters, category: "" });
      loadCategories(); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className={standalone ? "" : "mt-5"} data-testid="admin-faqs">
      {standalone && (
        <>
          <h1 className="font-heading text-2xl font-bold text-white">FAQs</h1>
          <p className="mt-1 text-sm text-slate-500">Manage public Help Center questions, categories, publishing and search insights.</p>
        </>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 rounded-lg border border-adminborder bg-adminbg px-3">
          <Search className="h-3.5 w-3.5 text-slate-500" />
          <input className="bg-transparent py-2 text-sm text-slate-200 outline-none" placeholder="Search questions…" data-testid="faqs-admin-search"
                 value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} />
        </div>
        <select className={`${darkInput} max-w-48`} data-testid="faqs-admin-category" value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}>
          <option value="">All Categories</option>{categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className={`${darkInput} max-w-36`} data-testid="faqs-admin-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
          <option value="">All Status</option><option value="published">Published</option><option value="draft">Draft</option>
        </select>
        <button onClick={() => setShowMetrics(!showMetrics)} data-testid="faqs-metrics-toggle"
                className="flex items-center gap-1.5 rounded-lg border border-adminborder px-3 py-2 text-xs font-bold text-slate-300 hover:border-saffron hover:text-saffron">
          <BarChart3 className="h-3.5 w-3.5" /> Analytics
        </button>
        <button onClick={() => setCatModal(true)} data-testid="faqs-categories-btn"
                className="flex items-center gap-1.5 rounded-lg border border-adminborder px-3 py-2 text-xs font-bold text-slate-300 hover:border-saffron hover:text-saffron">
          <Tags className="h-3.5 w-3.5" /> Categories
        </button>
        <button onClick={() => setForm({ ...emptyFaq, category: categoryOptions[0] || "General" })} data-testid="faqs-add-btn"
                className="ml-auto flex items-center gap-1.5 rounded-lg bg-saffron px-4 py-2 text-xs font-bold text-white hover:bg-saffron-hover">
          <Plus className="h-3.5 w-3.5" /> Add FAQ
        </button>
      </div>

      {showMetrics && metrics && (
        <div className="mt-4 grid gap-3 sm:grid-cols-3" data-testid="faqs-metrics">
          <div className="rounded-xl border border-adminborder bg-admincard p-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">FAQs</p>
            <p className="mt-1 font-heading text-xl font-extrabold text-white">{metrics.published} <span className="text-xs font-normal text-slate-500">published · {metrics.drafts} drafts · {metrics.total} total</span></p>
          </div>
          <div className="rounded-xl border border-adminborder bg-admincard p-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Most Viewed</p>
            {(metrics.most_viewed || []).slice(0, 3).map((v, i) => <p key={i} className="mt-1 line-clamp-1 text-[11px] text-slate-300">{v.views}× {v.question}</p>)}
            {!metrics.most_viewed?.length && <p className="mt-1 text-[11px] text-slate-500">No views recorded yet</p>}
          </div>
          <div className="rounded-xl border border-adminborder bg-admincard p-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Searched · No Result</p>
            {(metrics.top_searches || []).slice(0, 2).map((s, i) => <p key={i} className="mt-1 text-[11px] text-slate-300">"{s.q}" ({s.n}×)</p>)}
            {(metrics.zero_result_searches || []).slice(0, 2).map((s, i) => <p key={i} className="mt-1 text-[11px] text-red-400">"{s.q}" — no answer ({s.n}×)</p>)}
            {!metrics.top_searches?.length && !metrics.zero_result_searches?.length && <p className="mt-1 text-[11px] text-slate-500">No searches yet</p>}
          </div>
        </div>
      )}

      <div className="mt-4 space-y-2.5" data-testid="faqs-admin-list">
        {items.map((f) => (
          <div key={f.id} className="flex flex-wrap items-center gap-3 rounded-xl border border-adminborder bg-admincard p-4" data-testid={`faq-admin-row-${f.id}`}>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-white">{f.question}</p>
              <p className="mt-0.5 text-[11px] text-slate-500">{f.category} · order {f.display_order} · {f.views ?? 0} views{(f.keywords || []).length ? ` · ${f.keywords.join(", ")}` : ""}</p>
            </div>
            {f.featured && <Star className="h-4 w-4 text-saffron" />}
            <span className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${f.published ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-500/15 text-slate-400"}`}>{f.published ? "Live" : "Draft"}</span>
            <button onClick={() => setForm({ ...f, keywords: (f.keywords || []).join(", ") })} data-testid={`faq-admin-edit-${f.id}`}
                    className="rounded-lg border border-adminborder p-1.5 text-slate-300 hover:border-saffron hover:text-saffron"><Edit3 className="h-3.5 w-3.5" /></button>
            <button onClick={async () => { await api.post(`/faqs/admin/faqs/${f.id}/duplicate`); toast.success("Duplicated as draft"); load(); }}
                    data-testid={`faq-admin-dup-${f.id}`} className="rounded-lg border border-adminborder p-1.5 text-slate-300 hover:border-blue-400 hover:text-blue-400"><Copy className="h-3.5 w-3.5" /></button>
            <button onClick={() => del(f)} data-testid={`faq-admin-delete-${f.id}`} className="rounded-lg bg-red-500/10 p-1.5 text-red-400 hover:bg-red-500/20"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
        {!items.length && <div className="rounded-xl border border-adminborder bg-admincard p-12 text-center text-sm text-slate-500">No FAQs match.</div>}
      </div>

      {form && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 p-4" data-testid="faq-form-modal">
          <div className="mx-auto my-8 w-full max-w-xl rounded-xl border border-adminborder bg-admincard p-6">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-lg font-bold text-white">{form.id ? "Edit FAQ" : "Add FAQ"}</h3>
              <button onClick={() => setForm(null)} data-testid="faq-form-close" className="grid h-8 w-8 place-items-center rounded-lg bg-white/5 text-slate-400 hover:text-white"><X className="h-4 w-4" /></button>
            </div>
            <div className="mt-4 space-y-3">
              <input className={darkInput} data-testid="faq-form-question" placeholder="Question *" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} />
              <textarea rows={4} className={darkInput} data-testid="faq-form-answer" placeholder="Answer *" value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} />
              <div className="grid gap-3 sm:grid-cols-2">
                <select className={darkInput} data-testid="faq-form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <input type="number" className={darkInput} data-testid="faq-form-order" placeholder="Display order" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: e.target.value })} />
              </div>
              <input className={darkInput} data-testid="faq-form-keywords" placeholder="Keywords, comma separated (helps search)" value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} />
              <div className="grid gap-3 sm:grid-cols-2">
                <input className={darkInput} data-testid="faq-form-related-label" placeholder="Related link label (optional)" value={form.related_label} onChange={(e) => setForm({ ...form, related_label: e.target.value })} />
                <input className={darkInput} data-testid="faq-form-related-href" placeholder="Related URL e.g. /track" value={form.related_href} onChange={(e) => setForm({ ...form, related_href: e.target.value })} />
              </div>
              <div className="flex gap-4">
                {[["featured", "Featured (Most Asked)"], ["published", "Published"]].map(([k, l]) => (
                  <label key={k} className="flex cursor-pointer items-center gap-2 text-xs font-bold text-slate-300" data-testid={`faq-form-${k}`}>
                    <input type="checkbox" data-testid={`faq-form-${k}-input`} checked={!!form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.checked })} className="h-4 w-4 accent-saffron" /> {l}
                  </label>
                ))}
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => setForm(null)} data-testid="faq-form-cancel" className="rounded-lg border border-adminborder px-4 py-2 text-sm font-bold text-slate-300">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="faq-form-save"
                      className="flex items-center gap-2 rounded-lg bg-saffron px-5 py-2 text-sm font-bold text-white hover:bg-saffron-hover disabled:opacity-60">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} {form.id ? "Save Changes" : "Add FAQ"}
              </button>
            </div>
          </div>
        </div>
      )}

      {catModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black/60 p-4" data-testid="faq-category-modal">
          <div className="mx-auto my-8 w-full max-w-2xl rounded-xl border border-adminborder bg-admincard p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-heading text-lg font-bold text-white">FAQ Categories</h3>
                <p className="mt-0.5 text-xs text-slate-500">Rename, reorder or add categories. Used categories cannot be deleted.</p>
              </div>
              <button onClick={() => setCatModal(false)} data-testid="faq-category-close" className="grid h-8 w-8 place-items-center rounded-lg bg-white/5 text-slate-400 hover:text-white"><X className="h-4 w-4" /></button>
            </div>

            <div className="mt-5 flex flex-col gap-2 rounded-lg border border-adminborder bg-adminbg p-3 sm:flex-row">
              <input className={darkInput} data-testid="faq-category-name" placeholder="New category name" value={newCat.name}
                     onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} />
              <input type="number" className={`${darkInput} sm:w-28`} data-testid="faq-category-order" placeholder="Order" value={newCat.display_order}
                     onChange={(e) => setNewCat({ ...newCat, display_order: e.target.value })} />
              <button onClick={addCategory} disabled={!newCat.name.trim()} data-testid="faq-category-add-btn"
                      className="flex shrink-0 items-center justify-center gap-1.5 rounded-lg bg-saffron px-4 py-2 text-xs font-bold text-white hover:bg-saffron-hover disabled:opacity-50">
                <Plus className="h-3.5 w-3.5" /> Add
              </button>
            </div>

            <div className="mt-4 space-y-2" data-testid="faq-category-list">
              {categories.map((c) => (
                <div key={c.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-adminborder p-3" data-testid={`faq-category-row-${c.id}`}>
                  <input className={`${darkInput} min-w-44 flex-1`} data-testid={`faq-category-name-${c.id}`} value={catDrafts[c.id]?.name ?? c.name}
                         onChange={(e) => setCatDrafts({ ...catDrafts, [c.id]: { ...(catDrafts[c.id] || c), name: e.target.value } })} />
                  <input type="number" className={`${darkInput} w-24`} data-testid={`faq-category-order-${c.id}`} value={catDrafts[c.id]?.display_order ?? c.display_order ?? 0}
                         onChange={(e) => setCatDrafts({ ...catDrafts, [c.id]: { ...(catDrafts[c.id] || c), display_order: e.target.value } })} />
                  <span className="rounded-full bg-white/5 px-2.5 py-1 text-[10px] font-bold text-slate-400">{c.faq_count} FAQs</span>
                  <button onClick={() => saveCategory(c)} data-testid={`faq-category-save-${c.id}`}
                          className="rounded-lg border border-adminborder px-3 py-2 text-xs font-bold text-slate-300 hover:border-saffron hover:text-saffron">Save</button>
                  <button onClick={() => delCategory(c)} disabled={c.faq_count > 0} data-testid={`faq-category-delete-${c.id}`}
                          className="rounded-lg bg-red-500/10 p-2 text-red-400 hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-35"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
